import { useState } from 'react'
import { ShieldQuestion } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'
import { api } from '../services/api'
import { getDeviceId } from '../lib/deviceId'
import type { PendingDevice } from '../hooks/useDevicePendingApprovals'

interface Props {
  open: boolean
  pending: PendingDevice[]
  /** approve/deny 後に呼ぶ(RequireAuth が pending を再取得 → 空で gate 解除)。 */
  onChanged: () => void
}

/**
 * 初見デバイス **承認(triage)** モーダル (#819 B-main Phase 2b).
 *
 * 登録済 **trusted 端末**で、未信頼の新端末ログインがある時に RequireAuth が
 * 全画面 block(#791② と同じく triage を強制)。各 pending 端末を
 * approve(trusted 化・非破壊・1タップ)/ deny(= 心当たり無し → 全
 * credential 失効=破壊)で捌く。
 *
 * deny は破壊操作。memory `feedback_ato_detection_out_of_band` /#802 の
 * 「強制 modal に破壊ワンタップ禁止」に従い、**実行前に必ず確認 dialog**
 * を挟む(#802 は secondary な "noAction" を設定画面へ逃がしたが、本 modal
 * は triage が主目的で deny は意図された主操作なので、強い確認 dialog で
 * 誤タップを防ぐのが proportionate)。
 */
export default function DeviceApprovalPromptModal({ open, pending, onChanged }: Props) {
  const { t, locale } = useI18n()
  const [busyId, setBusyId] = useState<string | null>(null)

  if (!open) return null

  const fmt = (v: unknown): string => {
    if (!v) return '-'
    try {
      if (typeof v === 'string') {
        const d = new Date(v)
        return isNaN(d.getTime()) ? v : d.toLocaleString(locale)
      }
      const s = (v as { _seconds?: number })._seconds
      if (typeof s === 'number') return new Date(s * 1000).toLocaleString(locale)
    } catch {
      /* noop */
    }
    return '-'
  }

  const caller = (): string => {
    try {
      return getDeviceId()
    } catch {
      return ''
    }
  }

  const handleApprove = async (deviceId: string) => {
    if (busyId) return
    setBusyId(deviceId)
    try {
      await api.approveDevice(deviceId, caller())
      showToast({ type: 'success', message: t('deviceApproval.approvedToast') })
      onChanged()
    } catch (e) {
      console.error('[DeviceApproval] approve failed:', e)
      showToast({ type: 'error', message: t('deviceApproval.actionError') })
    } finally {
      setBusyId(null)
    }
  }

  const handleDeny = async (deviceId: string) => {
    if (busyId) return
    // 破壊操作 (全 credential 失効) — 実行前に必ず確認 (#802/ato memory)。
    if (!window.confirm(t('deviceApproval.denyConfirm'))) return
    setBusyId(deviceId)
    try {
      await api.denyDevice(deviceId, caller())
      showToast({ type: 'success', message: t('deviceApproval.deniedToast') })
      onChanged()
    } catch (e) {
      console.error('[DeviceApproval] deny failed:', e)
      showToast({ type: 'error', message: t('deviceApproval.actionError') })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100">
          <ShieldQuestion size={32} className="text-amber-600" />
        </div>

        <h3 className="font-display text-xl font-bold text-text-primary text-center mb-2">
          {t('deviceApproval.promptTitle')}
        </h3>
        <p className="font-body text-sm text-text-secondary text-center mb-5 leading-relaxed">
          {t('deviceApproval.promptDescription')}
        </p>

        <div className="space-y-3">
          {pending.map((p) => (
            <div
              key={p.deviceId}
              className="rounded-lg bg-gray-50 border border-gray-200 p-3"
            >
              <div className="text-xs font-body text-text-primary space-y-1 mb-3">
                <div>
                  <span className="text-text-secondary">
                    {t('deviceApproval.deviceLabel')}:{' '}
                  </span>
                  {p.deviceLabel || '-'}
                </div>
                <div>
                  <span className="text-text-secondary">
                    {t('deviceApproval.ipLabel')}:{' '}
                  </span>
                  {p.lastIpAddress || '-'}
                </div>
                <div>
                  <span className="text-text-secondary">
                    {t('deviceApproval.seenLabel')}:{' '}
                  </span>
                  {fmt(p.firstSeen)}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleApprove(p.deviceId)}
                  disabled={busyId !== null}
                  className={`flex-1 h-11 rounded-xl font-display font-bold text-sm transition-colors ${
                    busyId !== null
                      ? 'bg-gray-300 text-white cursor-not-allowed'
                      : 'bg-yokoso-red text-white tap-effect'
                  }`}
                >
                  {t('deviceApproval.approveAction')}
                </button>
                <button
                  onClick={() => handleDeny(p.deviceId)}
                  disabled={busyId !== null}
                  className="flex-1 h-11 rounded-xl border border-amber-300 font-body text-sm font-semibold text-amber-700 tap-effect disabled:opacity-50"
                >
                  {t('deviceApproval.denyAction')}
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="font-body text-[11px] text-text-secondary text-center mt-4 leading-relaxed">
          {t('deviceApproval.promptHint')}
        </p>
      </div>
    </div>
  )
}
